import { Car } from "@/lib/types";

/**
 * Renser HTML-tekst ved å fjerne tags og konvertere <br /> til linjeskift
 */
function sanitizeHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n') // Konverter <br> til linjeskift
    .replace(/<[^>]*>/g, '') // Fjern alle andre HTML-tags
    .replace(/&nbsp;/g, ' ') // Konverter &nbsp; til mellomrom
    .replace(/\n\s*\n\s*\n/g, '\n\n') // Fjern overflødige linjeskift
    .trim(); // Fjern whitespace i start og slutt
}

/**
 * Konverterer en Finn.no entry til vårt interne Car-objekt format
 * 
 * @param entry XML-node fra Finn.no's API
 * @returns Car-objekt
 */
export function parseFinnEntry(entry: Document | Element): Car {
  // Hjelpefunksjon for å hente XML-verdier
  const getValue = (selector: string): string | null => {
    const el = entry.querySelector(selector);
    return el ? el.textContent : null;
  };
  
  // Hjelpefunksjon for å hente flere verdier som array
  const getValues = (selector: string): string[] => {
    const elements = entry.querySelectorAll(selector);
    return Array.from(elements).map(el => el.textContent || "").filter(Boolean);
  };
  
  // Hjelpefunksjon for å hente category-verdier
  const getCategoryTerm = (scheme: string): string | null => {
    const categoryEl = entry.querySelector(`category[scheme="${scheme}"]`);
    return categoryEl?.getAttribute('term') || null;
  };
  
  // Hjelpefunksjon for å sjekke om et bilde er gyldig (ikke kart eller logo)
  const isValidImage = (el: Element): boolean => {
    const url = el.getAttribute('url') || "";
    if (url.includes('maptiles.finncdn.no')) return false;
    
    // Sjekk om bildet er en logo
    const isLogo = el.closest('author') !== null;
    return !isLogo;
  };
  
  // Hent første media:content/url eller tom streng
  const getFirstImage = (): string => {
    const imageEls = entry.querySelectorAll('media\\:content[url], content[url]');
    const firstValidImage = Array.from(imageEls).find(isValidImage);
    return firstValidImage?.getAttribute('url') || "";
  };
  
  // Hent alle media:content/url som array
  const getAllImages = (): string[] => {
    const imageEls = entry.querySelectorAll('media\\:content[url], content[url]');
    return Array.from(imageEls)
      .filter(isValidImage)
      .map(el => el.getAttribute('url') || "")
      .filter(Boolean);
  };
  
  // Hent finn:field-verdier
  const getField = (name: string): string | null => {
    const fieldEl = entry.querySelector(`finn\\:field[name="${name}"], field[name="${name}"]`);
    return fieldEl?.getAttribute('value') || fieldEl?.textContent || null;
  };
  
  // Hent finn:field med multiple verdier
  const getFieldValues = (name: string, subSelector = 'finn\\:value, value'): string[] => {
    const fieldEl = entry.querySelector(`finn\\:field[name="${name}"], field[name="${name}"]`);
    if (!fieldEl) return [];
    
    const valueEls = fieldEl.querySelectorAll(subSelector);
    if (valueEls.length > 0) {
      return Array.from(valueEls).map(el => el.textContent || "").filter(Boolean);
    }
    
    return fieldEl.textContent ? [fieldEl.textContent] : [];
  };
  
  // Hent engine-felter
  const getEngineField = (subField: string): string | null => {
    const engineEl = entry.querySelector('finn\\:field[name="engine"], field[name="engine"]');
    if (!engineEl) return null;
    
    const subFieldEl = engineEl.querySelector(`finn\\:field[name="${subField}"], field[name="${subField}"]`);
    return subFieldEl?.getAttribute('value') || null;
  };
  
  // Hent price-felter
  const getPrice = (name: string): number => {
    const priceEl = entry.querySelector(`finn\\:price[name="${name}"], price[name="${name}"]`);
    const value = priceEl?.getAttribute('value') || "0";
    return parseInt(value, 10) || 0;
  };
  
  // Hent ID fra urn:id:XXXXX format
  const getId = (): string => {
    const idText = getValue('id') || "";
    const idMatch = idText.match(/urn:id:(\d+)/);
    return idMatch ? idMatch[1] : "";
  };
  
  // Hent beskrivelsen og behandle den
  const rawDescription = getField('description') || undefined;
  const description = rawDescription ? {
    raw: rawDescription, // Original HTML-versjon
    text: sanitizeHtml(rawDescription) // Renset tekstversjon
  } : undefined;
  
  // Bygg Car-objektet
  const car: Car = {
    id: getId() || getValue('dc\\:identifier') || "",
    title: getValue('title')?.trim() || "",
    published: getValue('published') || undefined,
    updated: getValue('updated') || undefined,
    disposed: getCategoryTerm('urn:finn:ad:disposed') === 'true',
    finnUrl: entry.querySelector('link[rel="alternate"]')?.getAttribute('href') || undefined,
    price: getPrice('main'),
    image: getFirstImage(),
    images: getAllImages(),
    
    // Hovedinformasjon
    make: getField('make') || "",
    model: getField('model') || "",
    modelSpec: getField('model_spec') || undefined,
    year: parseInt(getField('year') || "0", 10) || 0,
    mileage: parseInt(getField('mileage') || "0", 10) || 0,
    
    // Tekniske spesifikasjoner
    transmission: getField('transmission') || undefined,
    fuelType: getEngineField('fuel') || undefined,
    condition: getField('condition') ? "Brukt" : undefined,
    bodyType: getField('body_type') || undefined,
    
    // Farger og design
    exteriorColor: getField('exterior_color') || undefined,
    interiorColor: getField('interior_color') || undefined,
    
    // Ekstra informasjon
    vin: getField('vin') || undefined,
    firstRegistration: getField('first_registration') || undefined,
    
    // Detaljer
    trim: getField('trim') || undefined,
    drivetrain: getField('wheel_drive') || undefined,
    wheelSets: getField('wheel_sets') ? parseInt(getField('wheel_sets') || "0", 10) : undefined,
    seats: getField('seats') ? parseInt(getField('seats') || "0", 10) : undefined,
    engineEffect: getEngineField('effect') ? parseInt(getEngineField('effect') || "0", 10) : undefined,
    engineVolume: getEngineField('volume') ? parseFloat(getEngineField('volume') || "0") : undefined,
    
    // Selger/forhandler
    dealerName: getValue('author name') || undefined,
    dealerType: getField('dealer_segment') || undefined,
    dealerPhone: getValue('finn\\:phone-number') || undefined,
    dealerAddress: getValue('finn\\:address') || undefined,
    dealerCity: getValue('finn\\:city') || undefined,
    
    // Andre felter
    stockNumber: getField('stock_number') || undefined,
    description, // Nå et objekt med både raw og text
    features: getFieldValues('equipment'),
    warranty: getField('warranty') || undefined,
    warrantyDuration: getField('warranty_duration') ? parseInt(getField('warranty_duration') || "0", 10) : undefined,
    warrantyDistance: getField('warranty_distance') ? parseInt(getField('warranty_distance') || "0", 10) : undefined,
    serviceDocuments: getFieldValues('service_documents'),
  };
  
  return car;
}

/**
 * Konverterer en Finn.no søkeresultat-feed til en liste av Car-objekter
 * 
 * @param xmlString XML-responsen fra Finn.no's API
 * @returns Array av Car-objekter
 */
export function parseFinnSearchResults(xmlString: string): Car[] {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");
    
    // Finn alle entries
    const entries = xmlDoc.querySelectorAll('entry');
    
    // Konverter hver entry til et Car-objekt
    return Array.from(entries).map(entry => parseFinnEntry(entry));
  } catch (error) {
    console.error("Feil ved parsing av Finn.no søkeresultater:", error);
    return [];
  }
}

/**
 * Konverterer en enkelt Finn.no annonse til et Car-objekt
 * 
 * @param xmlString XML-responsen fra Finn.no's API for en enkelt annonse
 * @returns Car-objekt
 */
export function parseFinnCar(xmlString: string): Car | null {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");
    
    // Konverter entry til et Car-objekt
    return parseFinnEntry(xmlDoc);
  } catch (error) {
    console.error("Feil ved parsing av Finn.no bildata:", error);
    return null;
  }
} 